/**
 * Public Parent Approval Page for Super-Credit purchases by minors.
 *
 * Route: /credit-consent/:token   (no auth)
 *
 * Flow:
 *   1. Parent sees student/plan/amount summary
 *   2. Picks email / WhatsApp / SMS → receives OTP
 *   3. Enters OTP → status flips to `otp_verified`
 *   4. Reviews & approves → triggers Razorpay (test-mode) settlement on the linked order
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Mail, MessageSquare, Smartphone, Loader2, CheckCircle2, XCircle, AlertTriangle, Wallet } from 'lucide-react';
import { notifyError, friendlyError } from '../lib/errors';
import Loader from '../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GREEN = '#34A853';
const SOFT = '#F0FDF4';
const DARK = '#0C1A12';

const CHANNELS = [
  { id: 'email',    label: 'Email',     icon: Mail,        sub: 'Send to your inbox' },
  { id: 'whatsapp', label: 'WhatsApp',  icon: MessageSquare,sub: 'Mocked in dev' },
  { id: 'sms',      label: 'SMS',       icon: Smartphone,  sub: 'Mocked in dev' },
];

export default function PublicCreditConsentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [channel, setChannel] = useState('email');
  const [otp, setOtp] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [paying, setPaying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [done, setDone] = useState(null);

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await axios.get(`${API}/public/credit-consent/${token}`);
      setData(data);
      if (data.status === 'otp_verified') setVerified(true);
      if (data.status === 'approved') setDone({ already: true });
    } catch (e) {
      setError(friendlyError(e, 'Approval link unavailable'));
    }
    setLoading(false);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [token]);

  const sendOtp = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/public/credit-consent/${token}/send-otp`, { channel });
      toast.success(`OTP sent via ${channel}`);
    } catch (e) {
      notifyError(e, 'OTP send failed');
    }
    setSending(false);
  };

  const verifyOtp = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/public/credit-consent/${token}/verify-otp`, { channel, otp });
      setVerified(true);
      toast.success('Identity verified');
    } catch (e) {
      notifyError(e, 'OTP verification failed');
    }
    setVerifying(false);
  };

  const approve = async () => {
    setPaying(true);
    try {
      const res = await axios.post(`${API}/public/credit-consent/${token}/approve-and-pay`, { agreed: true });
      setDone(res.data);
      toast.success('Approved! Credits added to your child.');
    } catch (e) {
      notifyError(e, 'Approval failed');
    }
    setPaying(false);
  };

  const reject = async () => {
    const reason = window.prompt('Reason (optional)') || null;
    try {
      await axios.post(`${API}/public/credit-consent/${token}/reject`, { reason });
      toast.success('Purchase rejected');
      reload();
    } catch (e) {
      notifyError(e, 'Failed to reject');
    }
  };

  if (loading) return <CenterLoader label="Loading purchase approval…" />;
  if (error)   return <ErrorView message={error} />;
  if (!data)   return null;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: SOFT }} data-testid="public-credit-consent">
      <div className="w-full px-4 py-3 border-b border-emerald-100 bg-white">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: GREEN }}>D</div>
            <div>
              <div className="text-sm font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>Deskey</div>
              <div className="text-[10px] text-emerald-700 -mt-0.5 tracking-wider uppercase">Parent Purchase Approval</div>
            </div>
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded text-white" style={{ background: done ? GREEN : verified ? '#F59E0B' : '#0EA5E9' }}>
            {done ? 'Paid' : verified ? 'OTP verified' : 'Pending OTP'}
          </span>
        </div>
      </div>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 sm:py-10">
        {done ? (
          <div className="rounded-2xl bg-white border-2 border-emerald-200 p-7 shadow-lg" data-testid="credit-paid-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: SOFT }}>
                <CheckCircle2 className="w-6 h-6" style={{ color: GREEN }} />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>
                  Purchase approved
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  ₹{data.amount_inr} paid · {data.credits} Super-Credits added to <strong>{data.student_name}</strong>'s Deskey account.
                </p>
                <p className="text-[11px] text-slate-500 mt-3">
                  Receipt will be emailed to <strong>{data.parent_email}</strong>. Thank you.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Summary card */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6 sm:p-8" data-testid="credit-summary-card">
              <h1 className="text-xl sm:text-2xl font-bold" style={{ color: DARK, fontFamily: 'Space Grotesk, sans-serif' }}>
                Approve <span style={{ color: GREEN }}>{data.student_name}'s</span> Super-Credit purchase
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Hi <strong>{data.parent_name}</strong>, your child wants to top up Super-Credits on Deskey.
                As per the DPDP Act, your live OTP approval is mandatory — and you'll be the payer.
              </p>

              <div className="grid sm:grid-cols-3 gap-2.5 mt-5">
                <Fact label="Plan" value={data.plan_id.replace('pack_', '') + ' credits'}/>
                <Fact label="Amount" value={`₹${data.amount_inr}`}/>
                <Fact label="Credits to add" value={data.credits}/>
              </div>
            </div>

            {/* OTP card */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mt-5 overflow-hidden" data-testid="credit-otp-card">
              <div className="px-6 pt-5 pb-3 border-b border-slate-100">
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Step 1 — verify it's really you
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">Pick a channel, receive a 6-digit OTP, and confirm.</p>
              </div>

              <div className="grid grid-cols-3 border-b border-slate-100">
                {CHANNELS.map((c) => (
                  <button key={c.id}
                    disabled={verified}
                    onClick={() => setChannel(c.id)}
                    data-testid={`credit-channel-${c.id}`}
                    className={`px-4 py-3 text-left transition-colors border-b-2 ${
                      channel === c.id ? 'border-emerald-500 bg-emerald-50/40' : 'border-transparent hover:bg-slate-50'
                    } disabled:opacity-50`}>
                    <div className="flex items-start gap-2.5">
                      <c.icon className="w-4 h-4 mt-0.5" style={{ color: channel === c.id ? GREEN : '#64748B' }} />
                      <div>
                        <div className="text-[13px] font-semibold text-slate-800">{c.label}</div>
                        <div className="text-[11px] text-slate-500 -mt-0.5">{c.sub}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="p-6">
                <div className="text-xs text-slate-500 mb-3">
                  {channel === 'email' && `Send to ${data.parent_email}`}
                  {channel === 'whatsapp' && `Send to WhatsApp ending …${data.parent_whatsapp_last4}`}
                  {channel === 'sms' && `Send to mobile ending …${data.parent_mobile_last4}`}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={sendOtp} disabled={sending || verified}
                          data-testid="credit-send-otp-btn"
                          className="h-10 px-4 text-sm font-semibold text-white rounded-lg"
                          style={{ background: GREEN, opacity: verified ? 0.5 : 1 }}>
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                    {verified ? 'OTP verified ✓' : 'Send OTP'}
                  </Button>

                  <div className="flex gap-2 items-center">
                    <Input type="text" inputMode="numeric" maxLength={6} placeholder="••••••"
                           disabled={verified}
                           value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                           data-testid="credit-otp-input"
                           className="font-mono text-center text-lg tracking-[0.5em] h-10 rounded-lg border-slate-200 max-w-[200px]"/>
                    <Button onClick={verifyOtp} disabled={verifying || verified || otp.length !== 6}
                            data-testid="credit-verify-otp-btn" variant="outline"
                            className="h-10 px-4 text-sm font-semibold rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                      {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : null}
                      Verify
                    </Button>
                  </div>
                </div>

              </div>
            </div>

            {/* Approve & pay */}
            <div className="rounded-2xl bg-white border border-slate-200 shadow-sm mt-5 p-6 sm:p-8" data-testid="credit-approve-card">
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Step 2 — approve and pay
              </h2>
              <p className="text-xs text-slate-500 mt-0.5 mb-4">You will be charged ₹{data.amount_inr} via Razorpay. Credits go to your child's account.</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={approve} disabled={!verified || paying}
                        data-testid="credit-approve-pay-btn"
                        className="flex-1 sm:flex-none h-12 px-6 text-sm font-bold text-white rounded-lg shadow-md hover:shadow-lg"
                        style={{ background: GREEN }}>
                  {paying ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Wallet className="w-4 h-4 mr-2"/>}
                  Approve & pay ₹{data.amount_inr}
                </Button>
                <Button onClick={reject} variant="outline" data-testid="credit-reject-btn"
                        className="h-12 px-5 text-sm text-slate-600 border-slate-200 hover:bg-red-50 hover:border-red-200 hover:text-red-700">
                  <XCircle className="w-4 h-4 mr-2"/> Decline
                </Button>
              </div>

              {!verified && <p className="text-[11px] text-amber-700 mt-3 flex items-center gap-1.5"><AlertTriangle className="w-3 h-3"/> Complete OTP verification first.</p>}
            </div>

            <p className="text-[11px] text-slate-500 mt-6 text-center">
              Approval link expires on {new Date(data.expires_at).toLocaleString(undefined, { hour12: true })}.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5">
      <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-[15px] font-bold text-slate-800 mt-0.5">{value}</div>
    </div>
  );
}
function CenterLoader({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: SOFT }}>
      <Loader label={label} />
    </div>
  );
}
function ErrorView({ message }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: SOFT }}>
      <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 shadow text-center">
        <XCircle className="w-8 h-8 text-red-500 mx-auto mb-2"/>
        <h3 className="text-base font-bold text-slate-900">Approval link unavailable</h3>
        <p className="text-sm text-slate-600 mt-1">{message}</p>
      </div>
    </div>
  );
}
